import React, { useState, useRef } from 'react';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Upload, Scan, Check, X, Loader2, FileImage,
  ArrowRight, Sparkles, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const ConfidenceBadge = ({ confidence }) => {
  const variants = {
    HIGH: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    MEDIUM: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    LOW: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  };
  return (
    <Badge variant="outline" className={variants[confidence] || variants.LOW}>
      {confidence}
    </Badge>
  );
};

const PaymentProofOCR = ({ 
  open, 
  onOpenChange, 
  vendors = [], 
  onPaymentCreated 
}) => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload, scanning, review, confirm
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [confidence, setConfidence] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [editedData, setEditedData] = useState({});

  const resetState = () => {
    setStep('upload');
    setImagePreview(null);
    setImageBase64(null);
    setExtractedData(null);
    setSuggestions([]);
    setConfidence(null);
    setSelectedVendor('');
    setEditedData({});
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setImagePreview(base64);
      setImageBase64(base64.split('base64,')[1]);
      setStep('review');
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    // Phase 3 (May 2026): extractPaymentProofOCR is 410 Gone. Payment
    // proof + method + reference are now captured directly in the
    // per-bill "Mark Paid" dialog, so the OCR-driven creation path is
    // deprecated. Surface a clean message and stop.
    toast.error(
      'Payment proof OCR is disabled. Use the "Mark Paid" button on each bill to record payments with reference / method / date fields.',
    );
    setStep('review');
  };

  const handleCreatePayment = async () => {
    // Phase 3 (May 2026): createPaymentFromOCR is 410 Gone. Migrate
    // operators to the per-bill Mark Paid flow.
    toast.error(
      'This flow has moved. Open the vendor page, pick the bill you paid, and click "Mark Paid" to record the payment with proof.',
    );
    resetState();
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl bg-card border-border" data-testid="ocr-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            AI Payment Proof Scanner
          </DialogTitle>
          <DialogDescription>
            Upload a payment proof image (UPI screenshot, bank transfer receipt) to automatically extract payment details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div 
                className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="ocr-file-input"
                />
                <FileImage className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Drop payment proof here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse (JPEG, PNG, WEBP)
                </p>
              </div>

              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Supported proof types:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• UPI payment screenshots (Google Pay, PhonePe, Paytm, etc.)</li>
                  <li>• Bank transfer confirmations (NEFT, RTGS, IMPS)</li>
                  <li>• Bank statement excerpts</li>
                  <li>• Payment receipts</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step: Review Image */}
          {step === 'review' && imagePreview && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden border border-border/50">
                <img 
                  src={imagePreview} 
                  alt="Payment proof preview" 
                  className="w-full max-h-[300px] object-contain bg-black/20"
                />
              </div>

              <div className="space-y-2">
                <Label>Pre-select vendor (optional - improves bill matching)</Label>
                <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                  <SelectTrigger data-testid="vendor-select">
                    <SelectValue placeholder="Select vendor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">No vendor selected</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={resetState} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleScan} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="scan-btn"
                >
                  <Scan className="w-4 h-4 mr-2" />
                  Scan with AI
                </Button>
              </div>
            </div>
          )}

          {/* Step: Scanning */}
          {step === 'scanning' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto text-emerald-400 animate-spin mb-4" />
              <p className="text-lg font-medium">Scanning payment proof...</p>
              <p className="text-sm text-muted-foreground mt-1">
                AI is extracting payment details from your image
              </p>
            </div>
          )}

          {/* Step: Confirm */}
          {step === 'confirm' && extractedData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Extracted Details</h4>
                <ConfidenceBadge confidence={confidence} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={editedData.amount || ''}
                      onChange={(e) => setEditedData({...editedData, amount: e.target.value})}
                      className="pl-8"
                      data-testid="amount-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Transaction Date</Label>
                  <Input
                    type="date"
                    value={editedData.transaction_date || ''}
                    onChange={(e) => setEditedData({...editedData, transaction_date: e.target.value})}
                    data-testid="date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference/UTR Number</Label>
                  <Input
                    value={editedData.reference_number || ''}
                    onChange={(e) => setEditedData({...editedData, reference_number: e.target.value})}
                    data-testid="reference-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select 
                    value={editedData.payment_method || 'BANK_TRANSFER'} 
                    onValueChange={(v) => setEditedData({...editedData, payment_method: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="NEFT">NEFT</SelectItem>
                      <SelectItem value="RTGS">RTGS</SelectItem>
                      <SelectItem value="IMPS">IMPS</SelectItem>
                      <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="CHEQUE">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Vendor Selection */}
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                  <SelectTrigger data-testid="vendor-confirm-select">
                    <SelectValue placeholder="Select vendor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bill allocation happens in the unified dialog after payment creation */}
              {suggestions.length > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {suggestions.length} matching bill(s) found — you'll allocate in the next step
                  </p>
                </div>
              )}

              {/* Additional extracted info */}
              {(editedData.payer_name || editedData.bank_name) && (
                <div className="bg-muted/30 rounded-lg p-3 text-sm">
                  {editedData.payer_name && (
                    <p><span className="text-muted-foreground">Payer:</span> {editedData.payer_name}</p>
                  )}
                  {editedData.bank_name && (
                    <p><span className="text-muted-foreground">Bank:</span> {editedData.bank_name}</p>
                  )}
                  {editedData.status && (
                    <p><span className="text-muted-foreground">Status:</span> {editedData.status}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('review')}>
                Back
              </Button>
              <Button 
                onClick={handleCreatePayment}
                disabled={creating || !selectedVendor}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-payment-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Create Payment
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentProofOCR;
